import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Text } from "../../Text/Text";
import { TextField } from "../../TextField";
import { DeleteTabButton } from "./DeleteTabButton/DeleteTabButton";
import "./Tab.scss";

const ENTER_KEY = "Enter";

export class Tab extends React.Component {
  static defaultProps = {
    disabled: false,
    editable: false,
    editMode: false,
  };

  static propTypes = {
    /** Additional class names for the component */
    className: PropTypes.string,
    /** Callback function for when tab is clicked */
    onClick: PropTypes.func,
    /** Whether the tab's label can be changed */
    disabled: PropTypes.bool,
    /** An icon element to be featured beside the Tab label */
    icon: PropTypes.node,
    /** Id associated with component */
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Label associated with component */
    label: PropTypes.string,
    /** True if the tab is the current open tab */
    active: PropTypes.bool,
    /** Flag for whether the tab’s label can be modified by the user or not. */
    editable: PropTypes.bool,
    /** Callback function for when tabs's label is changed */
    onLabelChange: PropTypes.func,
    /** a callback function that allows the consumer component to delete a tab */
    onDelete: PropTypes.func,
    /** a string the tab can display if the label prop is undefined */
    placeholderLabel: PropTypes.string,
    /** Allows the tab to start in edit mode */
    editMode: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      editMode: props.editMode,
      label: props.label,
    };

    this.inputRef = React.createRef();
  }

  componentDidMount() {
    const { editMode, editable } = this.props;
    this.setEditMode(editable && editMode);
  }

  componentDidUpdate(prevProps) {
    const { label } = this.props;

    if (prevProps.label !== label) {
      this.setState({ label });
    }
  }

  handleClickOutside = (event) => {
    if (this.inputRef && !this.inputRef.current.contains(event.target)) {
      this.setEditMode(false);
    }
  };

  setEditMode = (editMode) => {
    if (editMode) {
      document.addEventListener("mousedown", this.handleClickOutside);
    } else {
      document.removeEventListener("mousedown", this.handleClickOutside);
    }

    this.setState({ editMode });
  };

  onLabelChange = (newLabel) => {
    const { id, onLabelChange } = this.props;

    if (onLabelChange) {
      onLabelChange(id, newLabel);
    }

    this.setState({
      label: newLabel,
    });
  };

  onClick = () => {
    const { active, disabled, editable, id, onClick } = this.props;

    if (active && editable) {
      this.setEditMode(true);
    } else if (!disabled) {
      onClick(id);
    }
  };

  onKeyDown = (event) => {
    if (event.key === ENTER_KEY) {
      this.onClick();
    }
  };

  onDeleteClick = (event) => {
    const { id, onDelete } = this.props;

    event.stopPropagation();
    event.preventDefault();

    if (onDelete) {
      onDelete(id);
    }
  };

  render() {
    const {
      active,
      className,
      disabled,
      editable,
      icon,
      id,
      onDelete,
      placeholderLabel,
    } = this.props;

    const { editMode, label } = this.state;
    const tabClass = classNames(
      "Tab",
      {
        "Tab--active": active,
        "Tab--editMode": editMode && active,
        "Tab--disabled": disabled,
        "Tab--Editable": editable && active,
      },
      className
    );

    const labelShown = label || placeholderLabel;

    const textWeight = active ? "Medium" : "Regular";

    return (
      <div key={id} className={tabClass} data-testid="Tab">
        <div
          key={id}
          className="Tab__label"
          onClick={this.onClick}
          ref={this.inputRef}
          onKeyDown={this.onKeyDown}
          role="button"
          tabIndex={0}
          data-testid="Tab__label"
        >
          {editMode && active ? (
            <TextField
              key={id}
              defaultValue={label}
              onChange={this.onLabelChange}
              placeholder={placeholderLabel}
              autoFocus
              autoResize
            />
          ) : (
            <>
              {icon && <div className="label__icon">{icon}</div>}
              <Text key={id} type="P" weight={textWeight}>
                {labelShown}
              </Text>
            </>
          )}
          {onDelete && !editMode && (
            <div className="label__delete">
              {active && <DeleteTabButton onClick={this.onDeleteClick} />}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default Tab;
